import { Fragment } from 'react'
import { useAtomValue } from 'jotai'

import {
  IconCircleMinus,
  IconPlus,
  Button,
  TextFieldInput,
  GreyGrey60,
  GridItem
} from '@sequencehq/core-components'
import { LINE_ITEM_NAME_MAX_LENGTH } from '../../../../constants'
import {
  rateDisplayAtom,
  subItemsAtom,
  subItemsEnabledAtom
} from '../../state.atoms'
import { useSubItems } from './useSubItems'
import { QuantityField, RateField } from '../../../../common/fields'
import { isSubItemPartiallyCompleted } from './helpers'
import { validationResultsAtom } from '../../validation.atoms'
import { isFieldInvalid } from '../../../../common/validation'

export const SubItems = () => {
  const subItemsEnabled = useAtomValue(subItemsEnabledAtom)
  const subItems = useAtomValue(subItemsAtom)
  const rateDisplay = useAtomValue(rateDisplayAtom)
  const validationResults = useAtomValue(validationResultsAtom)

  const { addSubItem, updateSubItem, removeSubItem } = useSubItems()

  if (!subItemsEnabled) return null

  const firstSubItem = subItems.at(0)
  const lastSubItem = subItems.at(-1)

  return (
    <>
      {subItems.map((subItem, index) => {
        const isFirstSubItem = subItem.id === firstSubItem?.id
        const isLastSubItem = subItem.id === lastSubItem?.id

        const isRemoveEnabled =
          subItems.length > 1 &&
          (!isLastSubItem ||
            (isLastSubItem && isSubItemPartiallyCompleted(subItem)))

        const subItemValidationResults = validationResults?.subItems.at(index)

        return (
          <Fragment key={subItem.id}>
            <GridItem>
              {isRemoveEnabled && (
                <ButtonRemoveSubItem
                  onClick={() => removeSubItem(subItem.id)}
                />
              )}
            </GridItem>
            <GridItem>
              <TextFieldInput
                placeholder="Name"
                value={subItem.title}
                onChange={e =>
                  updateSubItem({ ...subItem, title: e.target.value })
                }
                maxLength={LINE_ITEM_NAME_MAX_LENGTH}
                autoFocus={isFirstSubItem}
                isInvalid={isFieldInvalid(subItemValidationResults?.title)}
              />
            </GridItem>
            <GridItem>
              <QuantityField
                value={subItem.quantity}
                onChange={value =>
                  updateSubItem({ ...subItem, quantity: value })
                }
                rateDisplay={rateDisplay}
                isInvalid={isFieldInvalid(subItemValidationResults?.quantity)}
              />
            </GridItem>
            <GridItem>
              <RateField
                value={subItem.rate}
                onChange={value => updateSubItem({ ...subItem, rate: value })}
                rateDisplay={rateDisplay}
                isInvalid={isFieldInvalid(subItemValidationResults?.rate)}
              />
            </GridItem>
          </Fragment>
        )
      })}

      <ButtonAddSubItem onClick={addSubItem} />
    </>
  )
}

const ButtonRemoveSubItem = ({ onClick }: { onClick: () => void }) => (
  <Button
    variant="unstyled"
    style={{
      padding: '0px',
      minWidth: 'initial',
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}
    color={GreyGrey60}
    onClick={onClick}
    data-testid="lineItemEditor.subItems.remove"
  >
    <IconCircleMinus color="inherit" />
  </Button>
)

const ButtonAddSubItem = ({ onClick }: { onClick: () => void }) => (
  <GridItem gridColumn="2 / span 3">
    <Button
      variant="secondary"
      fullWidth
      leadingIcon={<IconPlus color="inherit" />}
      onClick={onClick}
      data-testid="lineItemEditor.subItems.add"
    >
      Add another
    </Button>
  </GridItem>
)
